import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getVLANs, createVLAN, deleteVLAN, attachVM, detachVM } from "@/api/endpoints/vlans";
import type { VLANCreatePayload } from "@/types/vlan";
import { useActiveTenant } from "@/components/tenant/TenantSelector";

export function useVLANs() {
  const [tenantId] = useActiveTenant();

  return useQuery({
    queryKey: ["vlans", tenantId],
    queryFn: getVLANs,
    enabled: !!tenantId,
  });
}

export function useCreateVLAN() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: VLANCreatePayload) => createVLAN(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vlans"] });
      queryClient.invalidateQueries({ queryKey: ["billing-events"] });
      queryClient.invalidateQueries({ queryKey: ["billing-summary"] });
    },
  });
}

export function useDeleteVLAN() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vlanId: number) => deleteVLAN(vlanId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vlans"] });
      queryClient.invalidateQueries({ queryKey: ["billing-events"] });
      queryClient.invalidateQueries({ queryKey: ["billing-summary"] });
    },
  });
}

export function useAttachVM() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ vlanId, vmId }: { vlanId: number; vmId: number }) =>
      attachVM(vlanId, vmId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vlans"] });
      queryClient.invalidateQueries({ queryKey: ["vms"] });
      queryClient.invalidateQueries({ queryKey: ["vm"] });
    },
  });
}

export function useDetachVM() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ vlanId, vmId }: { vlanId: number; vmId: number }) =>
      detachVM(vlanId, vmId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vlans"] });
      queryClient.invalidateQueries({ queryKey: ["vms"] });
      queryClient.invalidateQueries({ queryKey: ["vm"] });
    },
  });
}
